/**
 * Constantes centralizadas do sistema de permissões RBAC.
 * Importadas por hooks e componentes — evita acoplamento a queries.
 */

// Tipo inline para evitar import circular com AuthContext
type UserRole = 'master' | 'vendedor_junior' | 'vendedor_senior' | 'financeiro_junior' | 'financeiro_senior' | 'gestor_comercial' | 'comprador' | 'estoquista';

export const ROLE_HIERARCHY: Record<UserRole, number> = {
  master: 100,
  gestor_comercial: 50,
  comprador: 45,
  estoquista: 40,
  vendedor_senior: 35,
  financeiro_senior: 35,
  vendedor_junior: 30,
  financeiro_junior: 30,
};

export interface FeatureModule {
  label: string;
  icon: string;
  keys: string[];
}

export const FEATURE_LABELS: Record<string, string> = {
  // Legacy keys
  view_sale_financials: 'Ver Financeiro da Venda',
  generate_pix: 'Gerar PIX',
  generate_boleto: 'Gerar Boleto',
  manual_receipt: 'Recebimento Manual',
  // CRM
  'crm:access': 'Acessar CRM',
  'crm:delete': 'Excluir Clientes',
  'crm:manage_portfolio': 'Gerenciar Carteira',
  'crm:manager_view': 'Visão Gerencial CRM',
  'crm:approve_registrations': 'Aprovar Cadastros',
  'crm:view_reports': 'Ver Relatório de Atendimentos',
  // Vendas
  'sales:discount': 'Aplicar Descontos',
  'sales:delete': 'Excluir Vendas',
  'sales:view_financials': 'Ver Financeiro (Vendas)',
  'sales:manage_policies': 'Gerenciar Políticas Comerciais',
  'sales:is_commissioned': 'É Comissionado',
  'sales:create': 'Criar Vendas',
  'sales:view_metrics': 'Visão de Métricas de Vendas',
  'sales:manage_goals': 'Gerenciar Metas',
  'sales:view_dashboard': 'Ver Painel de Vendas',
  'sales:edit_financial_category': 'Editar Categoria Financeira (Venda)',
  'sales:edit_receiving_account': 'Editar Conta de Recebimento',
  // Painel (Dashboard)
  'dashboard:view_sales': 'Visão de Vendas',
  'dashboard:view_financials': 'Visão Financeira',
  'dashboard:manage_goals': 'Gerenciar Metas',
  'dashboard:view_commissions': 'Ver Comissões',
  // Financeiro
  'fin:view_reports': 'Ver Relatórios Financeiros',
  'fin:receive_pay': 'Receber/Pagar',
  'fin:delete_renegotiate': 'Excluir/Renegociar Títulos',
  'fin:bank_sync': 'Sincronização Bancária',
  'finance:view_dre': 'Ver DRE Gerencial',
  'finance:view_cashflow': 'Ver Fluxo de Caixa',
  'finance:manage_dre': 'Gerenciar Estrutura do DRE',
  'finance:manage_receivables': 'Gerenciar contas a receber',
  'finance:manage_payables': 'Gerenciar contas a pagar',
  // PDV
  'pdv:manage_cash': 'Sangria e Suprimento de Caixa',
  'pdv:open_session': 'PDV — Abrir/Fechar Caixa',
  // Fiscal
  'fiscal:emit': 'Emitir NF-e/NFC-e',
  'fiscal:cancel': 'Cancelar NF-e',
  'fiscal:config': 'Configurações Fiscais',
  'fiscal:edit_number': 'Editar Numeração de NF-e',
  'fiscal:manage_sefaz_authorization': 'Gerenciar autorização SEFAZ-PR',
  // Logística
  'logistics:driver_app': 'App do Motorista',
  'logistics:manage': 'Gerenciar Logística',
  'logistics:view_reports': 'Ver Relatórios de Logística',
  'logistics:loan_view': 'Ver lista de saídas / romaneios',
  'logistics:loan_create': 'Criar nova saída (comodato)',
  'logistics:loan_sign': 'Assinar romaneio',
  'logistics:loan_send': 'Enviar romaneio por e-mail',
  'logistics:loan_delete': 'Excluir saída',
  'logistics:loan_return': 'Registrar devolução / recolhimento',
  'logistics:loan_consignation': 'Criar e gerenciar consignações',
  // NOTA: logistics:fuel_view é gate frontend-only
  // RLS SELECT usa fuel_manage|fleet_reports|(fuel_create+own)
  // fuel_view sem outra chave não acessa dados via RPC — tech debt M4
  'logistics:fuel_view': 'Ver abastecimentos',
  'logistics:fuel_create': 'Registrar abastecimento',
  'logistics:fuel_manage': 'Gerenciar abastecimentos',
  'logistics:fuel_cards_manage': 'Gerenciar cartões de combustível',
  'logistics:maintenance_view': 'Ver manutenções',
  'logistics:maintenance_manage': 'Gerenciar manutenções',
  'logistics:charges_view': 'Ver encargos de frota',
  'logistics:charges_create': 'Lançar encargos de frota',
  'logistics:charges_delete': 'Excluir encargos de frota',
  'logistics:fleet_reports': 'Relatórios de frota',
  // Logística — Viagens ao Fornecedor (Sprint D)
  'logistics:supplier_trip_view': 'Ver viagens ao fornecedor',
  'logistics:supplier_trip_create': 'Criar viagem ao fornecedor',
  'logistics:supplier_trip_manage': 'Gerenciar viagens e confirmar rateio de frete',
  'logistics:supplier_trip_driver': 'Executar viagem ao fornecedor (motorista)',
  // Locações
  'rentals:access': 'Acessar Locações',
  'rentals:manage_contracts': 'Gerenciar Contratos/Cilindros',
  'rentals:billing': 'Faturar Locações',
  'rentals:view_contracts': 'Visualizar Contratos de Locação',
  'rentals:approve_requests': 'Aprovar Pedidos de Locação',
  // Estoque/Compras
  'stock:manual_adjust': 'Ajuste Manual de Estoque',
  'stock:conference': 'Conferência de Estoque',
  'stock:movement_create': 'Movimentação manual de estoque',
  'purchasing:approve': 'Aprovar Compras',
  'purchasing:manage': 'Criar e Editar Compras',
  'products:manage_prices': 'Gerenciar Tabelas de Preço e Preços por Produto',
  'products:create': 'Criar Produtos e Kits',
  'products:import': 'Importar Produtos via Planilha',
  'products:print_labels': 'Imprimir Etiquetas de Produto',
  // Configurações
  'settings:manage_suppliers': 'Gerenciar Fornecedores/Transportadoras/Depósitos',
  'sales:manage_settings': 'Configurações de Vendas',
  'finance:manage_settings': 'Configurações Financeiras',
  // Tarefas
  'tasks:view':   'Visualizar módulo de tarefas',
  'tasks:create': 'Criar tarefas e projetos',
  'tasks:manage': 'Gerenciar tarefas de outros usuários',
  'tasks:assign': 'Delegar tarefas para outros usuários',
  // Projetos
  'projects:view':   'Visualizar projetos compartilhados',
  'projects:create': 'Criar projetos compartilhados',
  'projects:manage': 'Gerenciar projetos de outros usuários',
  // Admin
  'admin:users': 'Gerenciar Usuários',
  'admin:security': 'Configurações de Segurança',
  // E-commerce (Fase 1)
  'ecommerce:view': 'Ver Painel de Integrações E-commerce',
  'ecommerce:connect': 'Conectar / Desconectar Conta OAuth',
  'ecommerce:manage_products': 'Ativar / Desativar Produtos nos Canais',
  'ecommerce:view_orders': 'Ver Pedidos Importados dos Canais',
  'ecommerce:settings': 'Configurar Tabela de Preço e Toggles',
  // Multiloja — Organização (Sprint 1)
  'org:switch_context': 'Alternar Empresa (Switcher)',
  'org:view_consolidated': 'Ver Dashboard/DRE Consolidado do Grupo',
  'org:cross_stock_query': 'Consultar Estoque de Outras Unidades',
  'org:manage_transfers': 'Criar e Aprovar Transferências entre Unidades',
  'org:view_group_finance': 'Acessar Painel Financeiro do Grupo',
};

/**
 * Mapeamento de roles RBAC para labels amigáveis.
 * Inclui 'master' (Administrador) para uso em dropdowns de atribuição.
 */
export const ROLE_LABELS: Record<string, string> = {
  master: 'Administrador',
  vendedor_junior: 'Vendedor Júnior',
  vendedor_senior: 'Vendedor Sênior',
  financeiro_junior: 'Financeiro Júnior',
  financeiro_senior: 'Financeiro Sênior',
  gestor_comercial: 'Gestor Comercial',
  comprador: 'Comprador',
  estoquista: 'Estoquista',
};

/**
 * Roles que podem ser atribuídos a usuários na tela de gestão.
 * Inclui master (Administrador).
 */
export const ASSIGNABLE_ROLES = ROLE_LABELS;

export const CONFIGURABLE_ROLES = [
  'vendedor_junior',
  'vendedor_senior',
  'financeiro_junior',
  'financeiro_senior',
  'gestor_comercial',
  'comprador',
  'estoquista',
];

/**
 * Descrição curta exibida sob o rótulo no painel de permissões (só para chaves
 * cuja semântica não é óbvia). PDV-3 lote 2 (D3): pdv:open_session é
 * permissiva-quando-vazia — a RPC só gateia quando a empresa configurou a chave.
 */
export const FEATURE_HINTS: Record<string, string> = {
  'pdv:open_session':
    'Uma chave para operar a sessão de caixa (abrir e fechar). Sem configuração, todos os usuários podem abrir e fechar o caixa; ao marcar o primeiro cargo ou usuário, somente os marcados abrem e fecham.',
};

export const FEATURE_KEYS = [
  // Legacy
  'view_sale_financials', 'generate_pix', 'generate_boleto', 'manual_receipt',
  // CRM & Vendas
  'crm:access', 'crm:delete', 'crm:manage_portfolio', 'crm:manager_view', 'crm:approve_registrations', 'crm:view_reports',
  'sales:discount', 'sales:delete', 'sales:view_financials', 'sales:manage_policies', 'sales:is_commissioned', 'sales:create', 'sales:view_metrics', 'sales:manage_goals', 'sales:view_dashboard', 'sales:edit_financial_category', 'sales:edit_receiving_account',
  // Painel
  'dashboard:view_sales', 'dashboard:view_financials', 'dashboard:manage_goals', 'dashboard:view_commissions',
  // Financeiro
  'fin:view_reports', 'fin:receive_pay', 'fin:delete_renegotiate', 'fin:bank_sync',
  'finance:view_dre', 'finance:view_cashflow', 'finance:manage_dre', 'finance:manage_receivables', 'finance:manage_payables',
  // PDV
  'pdv:manage_cash', 'pdv:open_session',
  // Fiscal
  'fiscal:emit', 'fiscal:cancel', 'fiscal:config', 'fiscal:edit_number',
  'fiscal:manage_sefaz_authorization',
  // Logística
  'logistics:driver_app', 'logistics:manage', 'logistics:view_reports',
  'logistics:loan_view', 'logistics:loan_create', 'logistics:loan_sign', 'logistics:loan_send',
  'logistics:loan_delete', 'logistics:loan_return',
  'logistics:fuel_view', 'logistics:fuel_create', 'logistics:fuel_manage',
  'logistics:fuel_cards_manage',
  'logistics:maintenance_view', 'logistics:maintenance_manage',
  'logistics:charges_view', 'logistics:charges_create', 'logistics:charges_delete',
  'logistics:fleet_reports',
  'logistics:supplier_trip_view', 'logistics:supplier_trip_create',
  'logistics:supplier_trip_manage', 'logistics:supplier_trip_driver',
  // Locações
  'rentals:access', 'rentals:manage_contracts', 'rentals:billing',
  'rentals:view_contracts', 'rentals:approve_requests',
  // Estoque/Compras
  'stock:manual_adjust', 'stock:conference', 'stock:movement_create', 'purchasing:approve', 'purchasing:manage', 'products:manage_prices',
  'products:create', 'products:import', 'products:print_labels',
  // Configurações
  'settings:manage_suppliers',
  'sales:manage_settings',
  'finance:manage_settings',
  // Tarefas
  'tasks:view', 'tasks:create', 'tasks:manage', 'tasks:assign',
  // Projetos
  'projects:view', 'projects:create', 'projects:manage',
  // Admin
  'admin:users', 'admin:security',
  // E-commerce (Fase 1)
  'ecommerce:view', 'ecommerce:connect', 'ecommerce:manage_products',
  'ecommerce:view_orders', 'ecommerce:settings',
  // Multiloja — Organização (Sprint 1)
  'org:switch_context', 'org:view_consolidated', 'org:cross_stock_query',
  'org:manage_transfers', 'org:view_group_finance',
];

export const FEATURE_MODULES: Record<string, FeatureModule> = {
  crm_vendas: {
    label: 'CRM & Vendas',
    icon: 'ShoppingCart',
    keys: [
      'crm:access', 'crm:delete', 'crm:manage_portfolio', 'crm:manager_view',
      'crm:approve_registrations', 'crm:view_reports',
      'sales:discount', 'sales:delete', 'sales:manage_policies',
      'sales:is_commissioned', 'sales:create', 'sales:view_metrics', 'sales:manage_goals', 'sales:view_dashboard',
      'sales:edit_financial_category',
      'sales:edit_receiving_account',
    ],
  },
  painel: {
    label: 'Painel (Dashboard)',
    icon: 'LayoutDashboard',
    // dashboard:view_sales omitido da UI — redundante com sales:view_dashboard; chave permanece em FEATURE_KEYS
    keys: [
      'dashboard:view_financials',
      'dashboard:manage_goals', 'dashboard:view_commissions',
    ],
  },
  financeiro: {
    label: 'Financeiro',
    icon: 'DollarSign',
    keys: [
      'view_sale_financials', 'generate_pix', 'generate_boleto', 'manual_receipt',
      'fin:view_reports', 'fin:receive_pay', 'fin:delete_renegotiate', 'fin:bank_sync',
      'finance:view_dre', 'finance:view_cashflow', 'finance:manage_dre', 'finance:manage_receivables', 'finance:manage_payables',
    ],
  },
  pdv: {
    label: 'PDV',
    icon: 'Monitor',
    keys: ['pdv:manage_cash', 'pdv:open_session'],
  },
  fiscal: {
    label: 'Fiscal',
    icon: 'FileText',
    keys: ['fiscal:emit', 'fiscal:cancel', 'fiscal:config', 'fiscal:edit_number', 'fiscal:manage_sefaz_authorization'],
  },
  logistica: {
    label: 'Logística',
    icon: 'Truck',
    keys: [
      'logistics:driver_app', 'logistics:manage', 'logistics:view_reports',
      'logistics:loan_view', 'logistics:loan_create', 'logistics:loan_sign', 'logistics:loan_send',
      'logistics:loan_delete', 'logistics:loan_return',
      'logistics:fuel_view', 'logistics:fuel_create', 'logistics:fuel_manage',
      'logistics:fuel_cards_manage',
      'logistics:maintenance_view', 'logistics:maintenance_manage',
      'logistics:charges_view', 'logistics:charges_create', 'logistics:charges_delete',
      'logistics:fleet_reports',
      'logistics:supplier_trip_view', 'logistics:supplier_trip_create',
      'logistics:supplier_trip_manage', 'logistics:supplier_trip_driver',
    ],
  },
  locacoes: {
    label: 'Locações',
    icon: 'ClipboardList',
    keys: [
      'rentals:access', 'rentals:manage_contracts',
      'rentals:billing', 'rentals:view_contracts',
      'rentals:approve_requests',
    ],
  },
  estoque_compras: {
    label: 'Estoque & Compras',
    icon: 'Boxes',
    keys: [
      'stock:manual_adjust',
      'stock:conference',
      'stock:movement_create',
      'purchasing:approve',
      'purchasing:manage',
      'products:manage_prices',
      'products:create',
      'products:import',
      'products:print_labels',
    ],
  },
  configuracoes: {
    label: 'Configurações',
    icon: 'Settings',
    keys: [
      'settings:manage_suppliers',
      'sales:manage_settings',
      'finance:manage_settings',
    ],
  },
  administracao: {
    label: 'Administração',
    icon: 'Shield',
    keys: ['admin:users', 'admin:security'],
  },
  ecommerce: {
    label: 'E-commerce',
    icon: 'ShoppingBag',
    keys: [
      'ecommerce:view',
      'ecommerce:connect',
      'ecommerce:manage_products',
      'ecommerce:view_orders',
      'ecommerce:settings',
    ],
  },
  multiloja: {
    label: 'Multiloja (Grupo Empresarial)',
    icon: 'Building2',
    keys: [
      'org:switch_context',
      'org:view_consolidated',
      'org:cross_stock_query',
      'org:manage_transfers',
      'org:view_group_finance',
    ],
  },
  servicos: {
    label: 'Serviços',
    icon: 'Wrench',
    keys: [
      'services:view',
      'services:create',
      'services:approve',
      'services:convert',
      'services:order_delete',
      'services:manage_catalog',
    ],
  },
  tarefas: {
    label: 'Tarefas',
    icon: 'CheckSquare',
    keys: [
      'tasks:view',
      'tasks:create',
      'tasks:manage',
      'tasks:assign',
    ],
  },
  projetos: {
    label: 'Projetos',
    icon: 'Folders',
    keys: [
      'projects:view',
      'projects:create',
      'projects:manage',
    ],
  },
};
